import Button from "@/components/atom/Button";
import Input from "@/components/atom/Input";
import InputTitle from "@/components/sub-atom/InputTitle";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import currency from "@/functions/currency";
import { addBankMitra, addWithdrawel } from "@/functions/databaseData";
import fetchAPI from "@/functions/fetchAPI";
import useHeader from "@/functions/useHeader";
import useModal from "@/functions/useModal";
import { cn } from "@/utils/twMerge";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";

import { toast } from "react-toastify";

const settingToast = {
  position: "top-center",
  autoClose: 2000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined
};

export default function Withdrawel() {
  const { session } = useHeader();
  const { closeModal } = useModal();
  const [type, setType] = useState("withdraw");
  const [loading, setLoading] = useState({
    addBank: false,
    withdraw: false
  });
  const [data, setData] = useState({
    info_bank: {},
    account_number: 0,
    withdrawal_amount: 0,
    account_name: ""
  });
  const {
    data: listBankMitra,
    isLoading,
    refetch
  } = useQuery({
    queryFn: async () => {
      const datas = await fetchAPI({
        uri: `withdrawal/${session.user.data.mitra_id}/list/bank`,
        method: "GET",
        token: session?.user?.token
      });
      if (!datas?.error) return datas?.data;
      return [];
    },
    queryKey: ["list_bank_mitra_flip"],
    refetchOnWindowFocus: false
  });

  const {
    data: listBankFlip,
    isLoading: isLoadingFlip,
    refetch: refetchBankFlip
  } = useQuery({
    queryFn: async () => {
      const datas = await fetchAPI({
        uri: "withdrawal/list/bank",
        method: "GET",
        token: session?.user?.token
      });
      if (!datas?.error) return datas.data?.sender_bank;
      return [];
    },
    queryKey: ["list_bank_flip"],
    refetchOnWindowFocus: false
  });

  async function handleWithdraw() {
    setLoading((old) => ({ ...old, withdraw: true }));
    try {
      const isAmount = Number(
        data?.withdrawal_amount?.replace(/[^0-9]/g, "") || 0
      );
      if (isAmount < 15000)
        throw new Error(`minimal pencairan/penarikan ${currency(15000)}`);

      const res = await addWithdrawel({
        token: session?.user?.token,
        mitra_id: session.user.data.mitra_id, // didapat dari api profile
        withdrawal_amount: isAmount,
        account_name: data?.info_bank?.account_name,
        account_number: data?.info_bank?.account_number,
        bank_code: data?.info_bank?.bank_code,
        type: data?.info_bank?.type
      });

      if (!res?.error) {
        closeModal();
        toast.success(res?.message ?? "Success menambah pencairan", {
          position: "top-center"
        });
        return;
      }
      throw new Error(res?.error?.message);
    } catch (error) {
      toast.error(error.message, settingToast);
    } finally {
      setLoading((old) => ({ ...old, withdraw: false }));
    }
  }

  async function handleSaveBank() {
    setLoading((old) => ({ ...old, addBank: true }));
    try {
      const res = await addBankMitra({
        token: session?.user?.token,
        mitra_id: session.user.data.mitra_id, // didapat dari api profile
        account_name: data?.account_name,
        account_number: data?.account_number,
        bank_code: data?.info_bank?.bank_code,
        type: data?.info_bank?.type
      });

      console.log(res);
      if (!res?.error) {
        refetchBankFlip();
        closeModal();
        toast.success(res?.message ?? "Success menambah bank baru", {
          position: "top-center"
        });
        return;
      }
      throw new Error(res?.error?.message || res?.error);
    } catch (error) {
      console.log(error);
      toast.error(error.message, settingToast);
    } finally {
      setLoading((old) => ({ ...old, addBank: false }));
    }
  }

  const isListBankMitra = useMemo(() => {
    const groupedByStatus = listBankMitra
      ?.filter((a) => a.status !== "FAILED")
      .reduce((acc, item) => {
        const { status } = item;
        if (!acc[status]) {
          acc[status] = []; // Initialize an array if it doesn't exist
        }
        acc[status].push(item); // Add the item to the array
        return acc;
      }, {});
    return Object.entries(groupedByStatus || {});
  }, [listBankMitra, type]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <p className="subheading-semiBold text-greyscaleGON-700">
          Tarik Wallet
        </p>
      </div>
      {type == "withdraw" ? (
        <div className="flex flex-col gap-4">
          <div id="pilih-bank" className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-between">
              <InputTitle title={"Pilih Bank"} />
              <p
                id="tambah-bank"
                onClick={() => setType("addBank")}
                className="text-xs underline text-primaryGON-400 cursor-pointer"
              >
                Tambah Bank
              </p>
            </div>
            <Select
              disabled={isLoading}
              onValueChange={(a) => {
                const status = a.split("-")[1];
                const accountNumber = a.split("-")[0];

                if (status !== "ACTIVE")
                  return toast.error(
                    "Bank tidak bisa digunakan, pilih bank lainnya!!",
                    { position: "top-center" }
                  );

                setData((old) => ({
                  ...old,
                  info_bank: listBankMitra?.filter(
                    (aa) =>
                      aa.account_number == accountNumber &&
                      aa.status == "ACTIVE"
                  )[0]
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={isLoading ? "loading.." : "Select a bank"}
                />
              </SelectTrigger>
              <SelectContent>
                {(isListBankMitra || [])?.map(([status, datas], index) => (
                  <SelectGroup key={index}>
                    <SelectLabel>BANK {status}</SelectLabel>
                    {datas.map((a, b) => (
                      <SelectItem
                        key={b}
                        className={cn(
                          status !== "ACTIVE"
                            ? "select-none cursor-not-allowed pointer-events-none text-black/30"
                            : "cursor-pointer"
                        )}
                        value={`${a?.account_number}-${status}`}
                      >
                        {a?.account_name} - {a?.bank_name} - {a?.account_number}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input
            id="nominal-withdraw"
            label={"Nominal Penarikan"}
            onChange={(a) =>
              setData((old) => ({
                ...old,
                withdrawal_amount: a
              }))
            }
            value={data.withdrawal_amount <= 0 ? "" : data.withdrawal_amount}
            placeholder={currency(0)}
            type="currency"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <InputTitle title={"Pilih Bank"} />
            <Select
              disabled={isLoadingFlip}
              onValueChange={(a) =>
                setData((old) => ({
                  ...old,
                  info_bank: listBankFlip?.filter((aa) => aa.bank_code == a)[0]
                }))
              }
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={isLoadingFlip ? "loading..." : "Select a bank"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Bank Flip</SelectLabel>
                  {(listBankFlip || [])?.map((a, b) => {
                    return (
                      <SelectItem key={b} value={a?.bank_code}>
                        {a?.bank_name}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <Input
            label={"No Rekening"}
            onChange={(a) =>
              setData((old) => ({
                ...old,
                account_number: a
              }))
            }
            value={data.account_number}
            placeholder="masukkan nomor rekening"
            type="number"
          />
          <Input
            label={"Nama Rekening"}
            onChange={(a) =>
              setData((old) => ({
                ...old,
                account_name: a
              }))
            }
            value={data.account_name}
            placeholder="masukkan nama rekening"
          />
        </div>
      )}
      <div className="flex flex-col-reverse xs:flex-row w-full justify-between gap-4 items-center ">
        <Button
          label="Batalkan"
          variants="outline"
          disable={loading.withdraw || loading.addBank}
          onClick={() =>
            type == "addBank" ? setType("withdraw") : closeModal()
          }
          className="w-full"
        />
        {type == "withdraw" ? (
          <>
            <Button
              id="btn-handle-withdraw"
              label="Tarik Sekarang"
              disable={loading.withdraw}
              onClick={() => handleWithdraw()}
              className="w-full"
              loading={loading.withdraw}
            />
          </>
        ) : (
          <>
            <Button
              label="Tambah Bank"
              disable={loading.addBank}
              onClick={() => handleSaveBank()}
              className="w-full"
              loading={loading.addBank}
            />
          </>
        )}
      </div>
    </div>
  );
}
